import React, { useState, useEffect } from 'react';
import { 
  Typography, 
  Card, 
  Button, 
  Space, 
  Table, 
  Input, 
  Form, 
  Modal, 
  message, 
  Tag, 
  Tooltip,
  Popconfirm,
  Checkbox,
  Row,
  Col,
  Statistic
} from 'antd';
import { 
  FileTextOutlined, 
  SearchOutlined, 
  DeleteOutlined, 
  UserOutlined,
  EyeOutlined,
  LinkOutlined,
  CalendarOutlined,
  ReloadOutlined
} from '@ant-design/icons';
import { notesService } from '../../services/notesService';

const { Title, Text } = Typography;
const { Search } = Input;

const NotesManagement = () => {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchForm] = Form.useForm();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // 搜索参数
  const [searchParams, setSearchParams] = useState({
    userEmail: '',
    keyword: ''
  });

  // 获取笔记列表
  const fetchNotes = async (params = {}) => {
    setLoading(true);
    try {
      const response = await notesService.adminSearchNotes({
        ...searchParams,
        page: currentPage,
        limit: pageSize,
        ...params
      });
      
      setNotes(response.items || []);
      setTotal(response.total || 0);
    } catch (error) {
      console.error('获取笔记列表失败:', error);
      message.error('Failed to fetch notes');
    } finally {
      setLoading(false);
    }
  };

  // 组件加载时获取数据
  useEffect(() => {
    fetchNotes();
  }, [currentPage, pageSize]);

  // 处理搜索
  const handleSearch = (values) => {
    setSearchParams(values);
    setCurrentPage(1);
    fetchNotes(values);
  };

  // 处理重置搜索
  const handleReset = () => {
    searchForm.resetFields();
    setSearchParams({ userEmail: '', keyword: '' });
    setCurrentPage(1);
    fetchNotes({ userEmail: '', keyword: '' });
  };

  // 处理分页变化
  const handleTableChange = (pagination) => {
    setCurrentPage(pagination.current);
    setPageSize(pagination.pageSize);
  };

  // 处理单个删除
  const handleDelete = async (noteId) => {
    try {
      const result = await notesService.adminDeleteNote(noteId);
      if (result.success) {
        message.success('Deleted successfully');
        fetchNotes(); // 重新获取数据
      } else {
        message.error(result.message || 'Delete failed');
      }
    } catch (error) {
      console.error('删除笔记失败:', error);
      message.error('Delete failed');
    }
  };

  // 处理批量删除
  const handleBatchDelete = async () => {
    if (selectedRowKeys.length === 0) {
      message.warning('Please select notes to delete');
      return;
    }

    setDeleteLoading(true);
    try {
      const result = await notesService.adminBatchDeleteNotes(selectedRowKeys);
      if (result.success) {
        message.success(`Batch delete successful: ${result.successCount} items`);
        setSelectedRowKeys([]);
        setDeleteModalVisible(false);
        fetchNotes(); // 重新获取数据
      } else {
        message.error(result.message || 'Batch delete failed');
      }
    } catch (error) {
      console.error('批量删除失败:', error);
      message.error('Batch delete failed');
    } finally {
      setDeleteLoading(false);
    }
  };

  // 表格列定义
  const columns = [
    {
      title: 'Select',
      dataIndex: 'id',
      width: 60,
      render: (id) => (
        <Checkbox 
          checked={selectedRowKeys.includes(id)}
          onChange={(e) => {
            if (e.target.checked) {
              setSelectedRowKeys([...selectedRowKeys, id]);
            } else {
              setSelectedRowKeys(selectedRowKeys.filter(key => key !== id));
            }
          }}
        />
      )
    },
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      render: (title, record) => (
        <div>
          <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
            {title}
            {record.is_pinned && <Tag color="orange" style={{ marginLeft: '8px' }}>Pinned</Tag>}
            {record.is_public && <Tag color="green" style={{ marginLeft: '4px' }}>Public</Tag>}
          </div>
          {record.summary && (
            <Text type="secondary" style={{ fontSize: '12px' }}>
              {record.summary.length > 100 ? record.summary.substring(0, 100) + '...' : record.summary}
            </Text>
          )}
        </div>
      )
    },
    {
      title: 'User',
      dataIndex: 'owner_email',
      key: 'owner_email',
      render: (email, record) => (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '4px' }}>
            <UserOutlined style={{ marginRight: '4px' }} />
            <Text>{record.owner_username || email}</Text>
          </div>
          <Text type="secondary" style={{ fontSize: '12px' }}>{email}</Text>
        </div>
      )
    },
    {
      title: 'Source',
      dataIndex: 'source_type',
      key: 'source_type',
      render: (sourceType, record) => (
        <div>
          {sourceType && (
            <Tag color="blue">{sourceType}</Tag>
          )}
          {record.source_url && (
            <Tooltip title={record.source_url}>
              <LinkOutlined style={{ marginLeft: '8px', color: '#1890ff' }} />
            </Tooltip>
          )}
        </div>
      )
    },
    {
      title: 'Created At',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date) => (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <CalendarOutlined style={{ marginRight: '4px' }} />
          <Text>{new Date(date).toLocaleString('en-US')}</Text>
        </div>
      )
    },
    {
      title: 'Actions',
      key: 'action',
      width: 120,
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="View details">
            <Button 
              type="link" 
              icon={<EyeOutlined />} 
              size="small"
              onClick={() => {
                Modal.info({
                  title: record.title,
                  width: 800,
                  content: (
                    <div>
                      <p><strong>Summary:</strong> {record.summary || 'None'}</p>
                      <p><strong>Source:</strong> {record.source_url || 'None'}</p>
                      <p><strong>Created At:</strong> {new Date(record.created_at).toLocaleString('en-US')}</p>
                      <p><strong>Updated At:</strong> {new Date(record.updated_at).toLocaleString('en-US')}</p>
                    </div>
                  )
                });
              }}
            />
          </Tooltip>
          <Popconfirm
            title="Are you sure you want to delete this note?"
            onConfirm={() => handleDelete(record.id)}
            okText="Confirm"
            cancelText="Cancel"
          >
            <Button 
              type="link" 
              danger 
              icon={<DeleteOutlined />} 
              size="small"
            />
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <div className="admin-module">
      <div className="admin-module-header">
        <Title level={3} className="admin-module-title">Notes Management</Title>
        <Text className="admin-module-description">
          Manage all notes in the system, including searching, viewing, and deleting
        </Text>
      </div>

      {/* 统计信息 */}
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={6}>
          <Card>
            <Statistic
              title="Total Notes"
              value={total}
              prefix={<FileTextOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Current Page"
              value={notes.length}
              prefix={<EyeOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Selected"
              value={selectedRowKeys.length}
              prefix={<DeleteOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Public Notes"
              value={notes.filter(note => note.is_public).length}
              prefix={<LinkOutlined />}
            />
          </Card>
        </Col>
      </Row>

      {/* 搜索表单 */}
      <Card style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ whiteSpace: 'nowrap' }}>User Email:</span>
            <Input
              placeholder="Enter user email" 
              style={{ width: 200 }}
              allowClear
              value={searchParams.userEmail}
              onChange={(e) => setSearchParams({...searchParams, userEmail: e.target.value})}
            />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ whiteSpace: 'nowrap' }}>Keyword:</span>
            <Input
              placeholder="Enter keyword" 
              style={{ width: 200 }}
              allowClear
              value={searchParams.keyword}
              onChange={(e) => setSearchParams({...searchParams, keyword: e.target.value})}
            />
          </div>
          <Space>
            <Button type="primary" onClick={() => handleSearch(searchParams)} icon={<SearchOutlined />}>
              Search
            </Button>
            <Button onClick={handleReset} icon={<ReloadOutlined />}>
              Reset
            </Button>
          </Space>
        </div>
      </Card>

      {/* 操作按钮 */}
      <div style={{ marginBottom: 16 }}>
        <Space>
          <Button
            type="primary"
            danger
            disabled={selectedRowKeys.length === 0}
            onClick={() => setDeleteModalVisible(true)}
            icon={<DeleteOutlined />}
          >
            Batch Delete ({selectedRowKeys.length})
          </Button>
          <Button
            onClick={() => {
              setSelectedRowKeys([]);
              fetchNotes();
            }}
            icon={<ReloadOutlined />}
          >
            Refresh
          </Button>
        </Space>
      </div>

      {/* 笔记表格 */}
      <Table
        columns={columns}
        dataSource={notes}
        loading={loading}
        rowKey="id"
        pagination={{
          current: currentPage,
          pageSize: pageSize,
          total: total,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) =>
            `${range[0]}-${range[1]} of ${total} records`,
          pageSizeOptions: ['10', '20', '50', '100'],
        }}
        onChange={handleTableChange}
        rowSelection={{
          selectedRowKeys,
          onChange: setSelectedRowKeys,
        }}
      />

      {/* 批量删除确认弹窗 */}
      <Modal
        title="Confirm Batch Delete"
        open={deleteModalVisible}
        onOk={handleBatchDelete}
        onCancel={() => setDeleteModalVisible(false)}
        confirmLoading={deleteLoading}
        okText="Confirm Delete"
        cancelText="Cancel"
      >
        <p>Are you sure you want to delete the selected {selectedRowKeys.length} notes? This action cannot be undone.</p>
      </Modal>
    </div>
  );
};

export default NotesManagement; 